// ACTA_DAY_NIGHT_THEME_V52
// ACTA_SINGLE_THEME_V39B
// ACTA_THEME_BLUE_SKY_V38
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Thème utilisé par défaut lorsque le compte
 * ne possède pas encore de préférence enregistrée.
 */
export const DEFAULT_ACTA_THEME = "light";

/**
 * Ancienne clé globale.
 *
 * Elle est supprimée afin que deux comptes utilisant
 * le même navigateur ne partagent jamais leur thème.
 */
const LEGACY_STORAGE_KEY = "acta-theme";

/**
 * Thème de préchargement limité à l’onglet courant.
 *
 * Il évite le flash blanc avant l’hydratation React sans créer
 * une préférence globale partagée entre plusieurs comptes.
 */
export const THEME_BOOTSTRAP_SESSION_KEY =
  "acta-theme-bootstrap";

/**
 * Contexte React des thèmes de l'espace tenant.
 */
const ThemeContext = createContext(null);

/**
 * Thèmes disponibles pour les utilisateurs tenant.
 *
 * La Supervision SaaS Admin n'utilise pas cette liste :
 * elle dispose d'une apparence dédiée dans AppLayout.
 */
export const ACTA_THEMES = [
  {
    id: "light",
    name: "Jour — beige/cuivre",
    description:
      "Fond beige chaleureux, surfaces claires et accents cuivre.",
    swatches: ["#E8DDD0", "#F8F3EC", "#B7793F"],
  },
  {
    id: "dark",
    name: "Nuit — bleu nuit",
    description:
      "Fond bleu nuit, surfaces sombres et accents bleus.",
    swatches: ["#101724", "#1B2637", "#5C8FFF"],
  },
];

/**
 * Compatibilité avec les douze anciens thèmes Mat.
 */
const LEGACY_THEME_MAP = {
  light: "light",
  "blue-soft": "light",
  luxe: "light",
  sage: "light",
  dark: "dark",
  "night-comfort": "dark",
  mat1: "light",
  mat2: "light",
  mat3: "light",
  mat4: "light",
  mat5: "light",
  mat6: "light",
  mat7: "light",
  mat8: "light",
  mat9: "light",
  mat10: "light",
  mat11: "light",
  mat12: "light",
};

/**
 * Retourne vrai uniquement pour un thème réellement supporté.
 */
export function isValidActaTheme(value) {
  return value === "light" || value === "dark";
}

/**
 * Convertit une ancienne valeur ou une valeur invalide
 * vers un thème actuellement supporté.
 */
export function normalizeActaTheme(value) {
  const normalizedValue = String(value || "").trim();

  return (
    LEGACY_THEME_MAP[normalizedValue]
    || DEFAULT_ACTA_THEME
  );
}

/**
 * Génère une clé locale propre à chaque compte.
 *
 * Exemple :
 * acta-theme:usr_123
 */
export function getUserThemeStorageKey(userId) {
  return `acta-theme:${String(userId)}`;
}

/**
 * Lecture protégée de localStorage.
 */
function readLocalTheme(userId) {
  if (!userId) return null;

  try {
    const storedTheme = window.localStorage.getItem(
      getUserThemeStorageKey(userId)
    );

    return isValidActaTheme(storedTheme)
      ? normalizeActaTheme(storedTheme)
      : null;
  } catch {
    return null;
  }
}

/**
 * Écriture protégée dans localStorage.
 */
function writeLocalTheme(userId, theme) {
  if (!userId) return;

  const normalizedTheme =
    normalizeActaTheme(theme);

  try {
    window.localStorage.setItem(
      getUserThemeStorageKey(userId),
      normalizedTheme
    );

    window.sessionStorage.setItem(
      THEME_BOOTSTRAP_SESSION_KEY,
      normalizedTheme
    );
  } catch {
    // Le backend reste la source de vérité.
  }
}

/**
 * Provider global.
 *
 * L'état conserve aussi l'identifiant du propriétaire du thème.
 * Cela empêche d'afficher brièvement le thème du compte précédent.
 */
export function ThemeProvider({ children }) {
  const [state, setState] = useState({
    theme: DEFAULT_ACTA_THEME,
    ownerId: null,
    hydrated: false,
  });

  /**
   * Supprime l'ancienne clé globale non isolée.
   */
  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Aucune action nécessaire.
    }

    document.documentElement.removeAttribute("data-acta-theme");
  }, []);

  /**
   * Charge la préférence du compte connecté.
   *
   * Priorité :
   * 1. préférence enregistrée en base ;
   * 2. cache local propre à cet utilisateur ;
   * 3. thème par défaut.
   */
  const hydrateThemeForUser = useCallback((user) => {
    const userId = String(user?.id || "");

    if (!userId) {
      setState({
        theme: DEFAULT_ACTA_THEME,
        ownerId: null,
        hydrated: false,
      });

      return DEFAULT_ACTA_THEME;
    }

    const accountTheme = isValidActaTheme(user?.themePreference)
      ? normalizeActaTheme(user.themePreference)
      : null;

    const cachedTheme = readLocalTheme(userId);

    const nextTheme =
      accountTheme ||
      cachedTheme ||
      DEFAULT_ACTA_THEME;

    writeLocalTheme(userId, nextTheme);

    setState({
      theme: nextTheme,
      ownerId: userId,
      hydrated: true,
    });

    return nextTheme;
  }, []);

  /**
   * Applique immédiatement un thème au compte courant
   * et met à jour uniquement son cache local.
   *
   * L'enregistrement distant reste effectué par Settings.jsx.
   */
  const setThemeForUser = useCallback((userId, nextTheme) => {
    const normalizedUserId = String(userId || "");

    if (!normalizedUserId) {
      return DEFAULT_ACTA_THEME;
    }

    const normalizedTheme = normalizeActaTheme(nextTheme);

    writeLocalTheme(normalizedUserId, normalizedTheme);

    setState({
      theme: normalizedTheme,
      ownerId: normalizedUserId,
      hydrated: true,
    });

    return normalizedTheme;
  }, []);

  /**
   * Alias conservé pour les éventuels composants existants.
   *
   * Il ne peut écrire que dans le cache de l'utilisateur
   * déjà propriétaire de l'état courant.
   */
  const setTheme = useCallback((nextTheme) => {
    const normalizedTheme = normalizeActaTheme(nextTheme);

    setState((currentState) => {
      if (currentState.ownerId) {
        writeLocalTheme(
          currentState.ownerId,
          normalizedTheme
        );
      }

      return {
        ...currentState,
        theme: normalizedTheme,
      };
    });
  }, []);

  /**
   * Réinitialise l'état lors de la déconnexion.
   *
   * Le cache personnel reste conservé pour ce même compte.
   */
  const resetThemeSession = useCallback(() => {
    try {
      window.sessionStorage.removeItem(
        THEME_BOOTSTRAP_SESSION_KEY
      );
    } catch {
      // Aucune action nécessaire.
    }

    setState({
      theme: DEFAULT_ACTA_THEME,
      ownerId: null,
      hydrated: false,
    });
  }, []);

  const value = useMemo(
    () => ({
      theme: state.theme,
      themeOwnerId: state.ownerId,
      themeHydrated: state.hydrated,
      themes: ACTA_THEMES,
      setTheme,
      setThemeForUser,
      hydrateThemeForUser,
      resetThemeSession,
    }),
    [
      state,
      setTheme,
      setThemeForUser,
      hydrateThemeForUser,
      resetThemeSession]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook de consommation du thème.
 */
export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error(
      "useTheme must be used inside ThemeProvider"
    );
  }

  return context;
}
